/**
 * Loan Tab — Loan Terms + Key Ratios + Monthly Payment + Escrow waivers
 * Consumes API shape: { loan: LoanInfoViewModel }
 * Escrow waivers — GET loan: escrowPropertyTaxesWaiverIndicator, escrowPropertyInsuranceExFloodWaiverIndicator (bool?).
 */
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, Button, Card, CardContent, Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import EditableField from '../../../components/EditableField';
import StatusBadge, { LOAN_STATUS_MAP } from '../../../components/shared/StatusBadge';
import SectionHeader from '../../../components/shared/SectionHeader';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import PercentIcon from '@mui/icons-material/Percent';
import {
  NON_QM_MORTGAGE_TYPE_VALUE,
  getEffectiveMortgageType,
  resetNonQmFieldsForFormReset,
} from '../../../utils/nonQm';

import { fmtDollar as fmt, fmtPct as pct, fmtPricingPct } from '../../../utils/formatters';
import { getDisplayTotalMonthlyPayment, monthlyMortgageInsuranceLineLabel } from '../../../utils/monthlyPaymentDisplay';
import {
  formatLoanPurposeDisplay,
  getCashOutAmount,
  isCashOutRefinanceFromApi,
  REFINANCE_CASH_OUT_DETERMINATION_OPTIONS,
} from '../../../utils/loanPurposeDisplay';
import { getEstimatedCashToCloseDisplay } from '../../../utils/estimatedCashToCloseDisplay';

/** Reads boolean escrow flags from camelCase or PascalCase API payloads. */
const escrowWaiverValue = (loan, camel, pascal) => {
  if (!loan) return undefined;
  const v = loan[camel] ?? loan[pascal];
  return typeof v === 'boolean' ? v : undefined;
};

/**
 * EMD & seller-paid closing-cost fields (pro-api#127 / ENG-244). Centralized so the
 * canonical camelCase RHF path stays in sync with the backend LoanInfoViewModel field.
 */
const EARNEST_MONEY_FIELD = 'earnestMoneyDepositAmount';
const SELLER_PAID_CLOSING_COSTS_FIELD = 'sellerPaidClosingCostsAmount';

/** Reads a numeric loan amount from camelCase or PascalCase API payloads. */
const loanAmountValue = (loan, field) => {
  if (!loan) return undefined;
  const pascal = field.charAt(0).toUpperCase() + field.slice(1);
  return loan[field] ?? loan[pascal];
};

const EscrowWaiverCard = ({ label, name }) => {
  const { control } = useFormContext();
  return (
    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ flex: '1 1 200px', fontWeight: 500 }}>{label}</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={field.value === true ? 'yes' : field.value === false ? 'no' : null}
                onChange={(_, v) => {
                  if (v == null) return;
                  field.onChange(v === 'yes');
                }}
                sx={{ flexShrink: 0 }}
              >
                <ToggleButton value="yes" sx={{ px: 2.5, textTransform: 'none', fontWeight: 600 }}>Yes</ToggleButton>
                <ToggleButton value="no" sx={{ px: 2.5, textTransform: 'none', fontWeight: 600 }}>No</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        />
      </CardContent>
    </Card>
  );
};

const EscrowWaiverReadOnly = ({ label, value }) => (
  <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ flex: '1 1 200px', fontWeight: 500 }}>{label}</Typography>
        {value === true || value === false ? (
          <ToggleButtonGroup exclusive size="small" value={value ? 'yes' : 'no'} sx={{ flexShrink: 0, pointerEvents: 'none' }}>
            <ToggleButton value="yes" sx={{ px: 2.5, textTransform: 'none', fontWeight: 600 }}>Yes</ToggleButton>
            <ToggleButton value="no" sx={{ px: 2.5, textTransform: 'none', fontWeight: 600 }}>No</ToggleButton>
          </ToggleButtonGroup>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72, textAlign: 'right' }}>—</Typography>
        )}
      </Box>
    </CardContent>
  </Card>
);

import DataItem from '../../../components/shared/DataItem';

const MORTGAGE_TYPES = [
  { value: 'Conventional', label: 'Conventional' },
  { value: 'FHA', label: 'FHA' },
  { value: 'VA', label: 'VA' },
  { value: 'USDA', label: 'USDA' },
  { value: NON_QM_MORTGAGE_TYPE_VALUE, label: 'Non-QM' },
];

/** Vesta / Pro API: Purchase | Refinance only — cash-out vs rate/term is refinanceCashOutDeterminationType */
const LOAN_PURPOSE_TYPES = [
  { value: 'Purchase', label: 'Purchase' },
  { value: 'Refinance', label: 'Refinance' },
  { value: 'Construction', label: 'Construction' },
];

const AMORTIZATION_TYPES = [
  { value: 'FixedRateMortgage', label: 'Fixed' },
  { value: 'AdjustableRateMortgage', label: 'Adjustable Rate (ARM)' },
  { value: 'Other', label: 'Interest Only' },
];

const LoanTab = ({ loanDetail, isEditMode = false, onMortgageTypeChange, onNavigateToPricing }) => {
  const { setValue } = useFormContext();
  const currentMortgageType = useWatch({ name: 'loan.mortgageType' });
  const watchedLoanPurpose = useWatch({ name: 'loan.loanPurpose' });

  if (!loanDetail) return null;
  const l = loanDetail.loan || {};
  const sp = isEditMode ? 2 : 1;

  const waiveTaxes = escrowWaiverValue(l, 'escrowPropertyTaxesWaiverIndicator', 'EscrowPropertyTaxesWaiverIndicator');
  const waiveIns = escrowWaiverValue(l, 'escrowPropertyInsuranceExFloodWaiverIndicator', 'EscrowPropertyInsuranceExFloodWaiverIndicator');

  const showRefinanceDetermination =
    isEditMode && (watchedLoanPurpose === 'Refinance' || watchedLoanPurpose === 'CashOutRefinance');

  /** Server-side effective type ("Non-QM" if the indicator is true on the loaded loan). */
  const serverEffectiveMortgageType = getEffectiveMortgageType(l);
  const showStaleProductAlert = isEditMode
    && currentMortgageType != null
    && currentMortgageType !== serverEffectiveMortgageType
    && !!l.programName;

  /**
   * Mortgage Type dropdown drives the Non-QM indicator: picking "Non-QM" sets the
   * indicator to true (server still stores mortgageType=Conventional under the hood);
   * picking any real Vesta type clears the indicator and the four Non-QM derived fields.
   */
  const handleMortgageTypeSelect = (next) => {
    if (next === NON_QM_MORTGAGE_TYPE_VALUE) {
      setValue('loan.nonQualifiedMortgageIndicator', true, { shouldDirty: true });
    } else {
      setValue('loan.nonQualifiedMortgageIndicator', false, { shouldDirty: true });
      resetNonQmFieldsForFormReset(setValue);
    }
    onMortgageTypeChange?.(next);
  };

  return (
    <Grid container spacing={2}>
      {/* Loan Terms — lead with the decision-driving fields */}
      <Grid size={12}>
        <Card variant="outlined">
          <CardContent sx={{ pb: 1 }}>
            <SectionHeader title="Loan Terms" icon={<AccountBalanceIcon fontSize="small" />} />
            {showStaleProductAlert && l.programName && (
              <Alert
                severity="warning"
                icon={<WarningAmberIcon fontSize="small" />}
                sx={{ mb: 1.5 }}
                action={
                  <Button color="warning" size="small" variant="outlined" startIcon={<CalculateIcon />} onClick={() => onNavigateToPricing?.(l.mortgageType)}>
                    Go to Pricing
                  </Button>
                }
              >
                Mortgage type changed — the assigned product <strong>{l.programName}</strong> may no longer be valid. Save your changes, then assign a new product from Pricing.
              </Alert>
            )}
            <Grid container spacing={sp}>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <EditableField
                  label="Mortgage Type"
                  value={serverEffectiveMortgageType}
                  name="loan.mortgageType"
                  onChange={handleMortgageTypeSelect}
                  isEditMode={isEditMode}
                  options={isEditMode ? MORTGAGE_TYPES : null}
                  required={isEditMode}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <EditableField
                  label="Loan Purpose"
                  value={isEditMode ? l.loanPurpose : formatLoanPurposeDisplay(l)}
                  name="loan.loanPurpose"
                  isEditMode={isEditMode}
                  options={isEditMode ? LOAN_PURPOSE_TYPES : null}
                  onChange={(v) => {
                    if (v !== 'Refinance' && v !== 'CashOutRefinance') {
                      setValue('loan.refinanceCashOutDeterminationType', null);
                    }
                  }}
                />
              </Grid>
              {showRefinanceDetermination && (
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <EditableField
                    label="Refinance type"
                    value={l.refinanceCashOutDeterminationType}
                    name="loan.refinanceCashOutDeterminationType"
                    isEditMode
                    options={REFINANCE_CASH_OUT_DETERMINATION_OPTIONS}
                  />
                </Grid>
              )}
              {getCashOutAmount(l) != null && (
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  {isEditMode ? (
                    <EditableField
                      label={isCashOutRefinanceFromApi(l) ? 'Cash-out amount' : 'Est. cash to borrower (at closing)'}
                      value={getCashOutAmount(l)}
                      isEditMode={false}
                      type="currency"
                    />
                  ) : (
                    <DataItem label={isCashOutRefinanceFromApi(l) ? 'Cash-out amount' : 'Est. cash to borrower (at closing)'}>
                      {fmt(getCashOutAmount(l))}
                    </DataItem>
                  )}
                </Grid>
              )}
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <EditableField label="Loan Amount" value={l.loanAmount} name="loan.loanAmount" isEditMode={isEditMode} type="currency" required={isEditMode} placeholder="250,000" />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <EditableField label="Interest Rate" value={l.noteRate != null ? fmtPricingPct(l.noteRate) : null} isEditMode={false} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                {isEditMode ? (
                  <EditableField label="Term (months)" value={l.loanTermMonths} name="loan.loanTermMonths" isEditMode type="number" placeholder="360" />
                ) : (
                  <DataItem label="Term">{l.loanTermMonths ? `${l.loanTermMonths} mo (${l.loanTermMonths / 12} yr)` : '—'}</DataItem>
                )}
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <EditableField label="Amortization" value={l.amortizationType} name="loan.amortizationType" isEditMode={isEditMode} options={isEditMode ? AMORTIZATION_TYPES : null} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <Box sx={{ py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Loan Program</Typography>
                  {l.programName ? (
                    <Typography variant="body2" fontWeight={700} color="primary">{l.programName}</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: isEditMode ? 0 : 0.5 }}>
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">Not assigned</Typography>
                      <Button size="small" variant="outlined" startIcon={<CalculateIcon />} onClick={onNavigateToPricing}>Assign from Pricing</Button>
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <Box sx={{ py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Stage</Typography>
                  <Box><StatusBadge status={l.stage} map={LOAN_STATUS_MAP}>{l.stage || '—'}</StatusBadge></Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <EditableField label="Est. closing" value={l.estimatedCloseDate} name="loan.estimatedCloseDate" isEditMode={isEditMode} type="date" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Key Ratios & Down Payment — broker checks eligibility first */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent sx={{ pb: 1 }}>
            <SectionHeader title="Key Ratios" icon={<PercentIcon fontSize="small" />} />
            <Grid container spacing={sp}>
              <Grid size={6}><DataItem label="LTV" bold>{pct(l.ltvRatio)}</DataItem></Grid>
              <Grid size={6}><DataItem label="CLTV" bold>{pct(l.cltvRatio)}</DataItem></Grid>
              <Grid size={6}><DataItem label="DTI" bold color={l.dtiRatio > 43 ? 'warning.main' : 'success.main'}>{pct(l.dtiRatio)}</DataItem></Grid>
              <Grid size={6}>
                <DataItem label="Down Payment">
                  {fmt(l.downPaymentAmount)}
                  {l.downPaymentPercent != null && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>({l.downPaymentPercent.toFixed(1)}%)</Typography>}
                </DataItem>
              </Grid>
              <Grid size={6}>
                {(() => {
                  const etc = getEstimatedCashToCloseDisplay(l.estimatedCashToClose);
                  if (!etc) return <DataItem label="Est. cash to close">—</DataItem>;
                  return (
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Est. cash to close</Typography>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color={
                          etc.emphasis === 'borrower'
                            ? 'success.main'
                            : etc.emphasis === 'fromBorrower'
                              ? 'text.primary'
                              : 'text.primary'
                        }
                      >
                        {etc.label}: {etc.amountDisplay}
                      </Typography>
                    </Box>
                  );
                })()}
              </Grid>
              <Grid size={6}>
                <EditableField
                  label="Earnest Money Deposit"
                  value={loanAmountValue(l, EARNEST_MONEY_FIELD)}
                  name={`loan.${EARNEST_MONEY_FIELD}`}
                  isEditMode={isEditMode}
                  type="currency"
                  placeholder="10,000"
                />
              </Grid>
              <Grid size={6}>
                <EditableField
                  label="Seller-Paid Closing Costs"
                  value={loanAmountValue(l, SELLER_PAID_CLOSING_COSTS_FIELD)}
                  name={`loan.${SELLER_PAID_CLOSING_COSTS_FIELD}`}
                  isEditMode={isEditMode}
                  type="currency"
                  placeholder="5,000"
                />
              </Grid>
              <Grid size={6}><EditableField label="Broker Compensation" value={l.brokerCompensation != null ? fmtPricingPct(l.brokerCompensation) : null} isEditMode={false} /></Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Monthly Payment */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent sx={{ pb: 1 }}>
            <SectionHeader title="Monthly Payment" icon={<PaymentsIcon fontSize="small" />} />
            <Grid container spacing={sp}>
              <Grid size={6}><EditableField label="P&I" value={l.monthlyPrincipalAndInterest} isEditMode={false} type="currency" /></Grid>
              <Grid size={6}>
                <EditableField label="Taxes" value={l.monthlyTaxes} name="loan.monthlyTaxes" isEditMode={isEditMode} type="currency" decimalPlaces={2} />
              </Grid>
              <Grid size={6}>
                <EditableField label="Insurance" value={l.monthlyInsurance} name="loan.monthlyInsurance" isEditMode={isEditMode} type="currency" decimalPlaces={2} />
              </Grid>
              <Grid size={6}><EditableField label={monthlyMortgageInsuranceLineLabel(l.mortgageType)} value={l.monthlyMortgageInsurance} isEditMode={false} type="currency" /></Grid>
              <Grid size={6}>
                <EditableField label="HOA" value={l.monthlyHoaDues} name="loan.monthlyHoaDues" isEditMode={isEditMode} type="currency" decimalPlaces={2} />
              </Grid>
              <Grid size={6}><DataItem label="Total Payment" bold color="primary">{fmt(getDisplayTotalMonthlyPayment(l))}</DataItem></Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={12}>
        <Box>
          <SectionHeader title="Escrow waivers" icon={<SavingsOutlinedIcon fontSize="small" />} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              {isEditMode ? (
                <EscrowWaiverCard
                  label="Waive for all property taxes?"
                  name="loan.escrowPropertyTaxesWaiverIndicator"
                />
              ) : (
                <EscrowWaiverReadOnly label="Waive for all property taxes?" value={waiveTaxes} />
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {isEditMode ? (
                <EscrowWaiverCard
                  label="Waive for property insurances excluding flood?"
                  name="loan.escrowPropertyInsuranceExFloodWaiverIndicator"
                />
              ) : (
                <EscrowWaiverReadOnly label="Waive for property insurances excluding flood?" value={waiveIns} />
              )}
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
};

export default LoanTab;
